import {
  IndicationSheetInfo,
  IndicationSheetRow,
} from '@/modules/leadIndication/entities/leadIndication.interface';
import { useCallback, useEffect, useState } from 'react';
import * as parserSheet from 'xlsx';

type SheetCellValue = string | number | null;
type SheetRecord = Record<string, SheetCellValue>;

export interface UseIndicationSheetRowsOptions {
  /** Arquivo de planilha (CSV/XLSX) a ser lido. */
  file?: File | null;
  /** Número máximo de linhas de dados a serem retornadas em `rows`. */
  maxRows: number;
}

const INITIAL_SHEET_INFO: IndicationSheetInfo = {
  rows: [],
  rowCount: 0,
  totalRows: 0,
};

/** Ordem posicional dos campos usada como fallback quando o cabeçalho não é reconhecido. */
const FIELD_ORDER: (keyof IndicationSheetRow)[] = ['name', 'email', 'cpf'];

/** Aliases de cabeçalho aceitos para cada campo do lead (normalizados). */
const HEADER_ALIASES: Record<keyof IndicationSheetRow, string[]> = {
  name: ['nome', 'name'],
  email: ['email', 'e-mail'],
  cpf: ['cpf'],
};

/** Normaliza um cabeçalho: minúsculas, sem acentos e sem espaços nas bordas. */
const normalizeHeader = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const toCellString = (value: SheetCellValue): string => (value == null ? '' : String(value).trim());

/** Mapeia uma linha (objeto com chaves de cabeçalho) para o formato de lead. */
const mapRowToLead = (record: SheetRecord): IndicationSheetRow => {
  const entries = Object.entries(record);
  const result = {} as IndicationSheetRow;

  FIELD_ORDER.forEach((field, fallbackIndex) => {
    const matched = entries.find(([key]) =>
      HEADER_ALIASES[field].includes(normalizeHeader(key)),
    );
    const value = matched ? matched[1] : entries[fallbackIndex]?.[1];
    result[field] = toCellString(value ?? null);
  });

  return result;
};

/**
 * Lê o arquivo de planilha referenciado em `file` e retorna as linhas de dados
 * já mapeadas para o formato de lead (`name`, `email`, `cpf`), limitado ao
 * número máximo informado em `maxRows`.
 *
 * Suporta os formatos aceitos pela lib `xlsx` (CSV, XLS, XLSX). A primeira
 * linha da planilha é sempre tratada como cabeçalho, e as colunas são mapeadas
 * pelo nome (`Nome` -> `name`, `E-mail` -> `email`, `CPF` -> `cpf`).
 *
 * `totalRows` reflete o total de linhas de dados na planilha (antes do limite),
 * enquanto `rowCount` reflete quantas linhas foram efetivamente retornadas em
 * `rows`.
 */
const useIndicationSheetRows = ({ file, maxRows }: UseIndicationSheetRowsOptions) => {
  const [sheetInfo, setSheetInfo] = useState<IndicationSheetInfo>(INITIAL_SHEET_INFO);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const readRows = useCallback(
    async (fileToRead: File): Promise<IndicationSheetInfo> => {
      const buffer = await fileToRead.arrayBuffer();
      const workbook = parserSheet.read(buffer, { type: 'array' });

      const [firstSheetName] = workbook.SheetNames;
      const worksheet = workbook.Sheets[firstSheetName];

      if (!worksheet) {
        return INITIAL_SHEET_INFO;
      }

      const parsedRows = parserSheet.utils.sheet_to_json<SheetRecord>(worksheet, {
        blankrows: false,
        defval: null,
      });

      const limit = Math.max(maxRows, 0);
      const rows = parsedRows.slice(0, limit).map(mapRowToLead);

      return {
        rows,
        rowCount: rows.length,
        totalRows: parsedRows.length,
      };
    },
    [maxRows],
  );

  const readFile = useCallback(
    async (fileToRead: File): Promise<IndicationSheetInfo> => {
      setLoading(true);
      setError(null);

      try {
        const info = await readRows(fileToRead);
        setSheetInfo(info);
        return info;
      } catch (e) {
        console.error(e);
        setError(e as Error);
        setSheetInfo(INITIAL_SHEET_INFO);
        return INITIAL_SHEET_INFO;
      } finally {
        setLoading(false);
      }
    },
    [readRows],
  );

  useEffect(() => {
    if (!file) {
      setSheetInfo(INITIAL_SHEET_INFO);
      setError(null);
      return;
    }

    readFile(file);
  }, [file, readFile]);

  return {
    ...sheetInfo,
    loading,
    error,
    readFile,
  };
};

export default useIndicationSheetRows;
